/**
 * Utility functions for working with representative data
 */

/**
 * Normalize address from Google Places object
 * @param {Object} place - Google Places object
 * @returns {Promise<string>} - Normalized address string
 */
export async function normalizeAddressFromPlace(place) {
  // Prefer Google-provided formatted address (most accurate)
  if (place?.formatted_address) return place.formatted_address;

  // Fallback: Geocode by place_id (rare case)
  try {
    const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || "AIzaSyC86MKVZQRT_PWcrEx3IJU418vo10I5iSs";
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?place_id=${encodeURIComponent(place.place_id)}&key=${API_KEY}`
    );
    const data = await res.json();
    return data?.results?.[0]?.formatted_address || "";
  } catch (error) {
    console.error("Error normalizing address:", error);
    throw new Error("Failed to normalize address");
  }
}

/**
 * Fetch representatives data from Google Civic Information API via our Netlify proxy function
 * @param {string} address - Normalized address string
 * @returns {Promise<Object>} - Civic API response
 */
export async function fetchCivicData(address) {
  if (!address) {
    throw new Error("Address is required");
  }

  try {
    console.log("Fetching civic data for address:", address);
    
    // Use the current domain for the API call
    const baseUrl = window.location.origin;
    const proxyUrl = `${baseUrl}/.netlify/functions/civic?address=${encodeURIComponent(address)}`;
    
    console.log("Making request to:", proxyUrl);
    
    // Make the request to our Netlify function
    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    console.log("Response status:", response.status);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));
    
    // Get response text first for better error handling
    const responseText = await response.text();
    console.log("Response text preview:", responseText.substring(0, 200));
    
    if (!response.ok) {
      console.error("API request failed:", response.status, responseText);
      
      // Handle specific error cases
      if (response.status === 404) {
        throw new Error("Representatives lookup service is not available. This feature requires deployment to Netlify with proper function configuration.");
      } else if (response.status === 500) {
        // Try to parse error message from response
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.message || "Server configuration error. Please contact the administrator.");
        } catch (parseError) {
          throw new Error("Server configuration error. Please contact the administrator.");
        }
      } else {
        throw new Error(`Failed to fetch representatives (Status: ${response.status}): ${responseText}`);
      }
    }

    // Try to parse as JSON
    try {
      const data = JSON.parse(responseText);
      
      if (data.error) {
        console.error("Civic API returned error:", data.error);
        throw new Error(data.error.message || data.message || "Failed to fetch representatives");
      }
      
      return data;
    } catch (parseError) {
      console.error("Error parsing API response:", parseError);
      throw new Error("Invalid response format from representatives service");
    }
    
  } catch (error) {
    console.error("Error fetching civic data:", error);
    throw error;
  }
}

/**
 * Join offices and officials from Civic API response
 * @param {Object} data - Civic API response
 * @returns {Array} - Joined representatives data
 */
export function joinOfficesAndOfficials(data) {
  const { offices = [], officials = [], divisions = {} } = data;
  const items = [];

  // Process each office and link to officials
  for (const office of offices) {
    const division = divisions[office.divisionId] || {};
    
    // Skip if no official indices
    if (!office.officialIndices || !Array.isArray(office.officialIndices)) {
      continue;
    }
    
    for (const idx of office.officialIndices) {
      // Skip if index is invalid
      if (idx < 0 || idx >= officials.length) {
        continue;
      }
      
      const official = officials[idx];
      items.push({
        officeName: office.name, // e.g. "United States House of Representatives"
        levels: office.levels || [], // e.g. ["country"]
        roles: office.roles || [], // e.g. ["legislatorLowerBody"]
        divisionId: office.divisionId, // parse for districts
        divisionName: division.name || "",
        name: official.name,
        party: official.party || "",
        phones: official.phones || [],
        urls: official.urls || [],
        emails: official.emails || [],
        photoUrl: official.photoUrl || null,
        channels: official.channels || [], // social media
        address: official.address && official.address[0]
      });
    }
  }

  return items;
}

/**
 * Extract district information from divisionId
 * @param {string} divisionId - Division ID from Civic API
 * @returns {Object} - District information
 */
export function extractDistrict(divisionId) {
  if (!divisionId) return null;
  
  // Examples:
  // - federal House: ocd-division/country:us/state:va/cd:2
  // - state lower: ocd-division/country:us/state:va/sldl:96
  // - state upper: ocd-division/country:us/state:va/sldu:25
  
  const mCD = divisionId.match(/\/cd:(\w+)/); // U.S. House
  const mSLDL = divisionId.match(/\/sldl:(\w+)/); // State lower (house/assembly)
  const mSLDU = divisionId.match(/\/sldu:(\w+)/); // State upper (senate)
  const mState = divisionId.match(/\/state:(\w{2})/); // State code
  
  return {
    usHouse: mCD ? mCD[1] : null,
    stateLower: mSLDL ? mSLDL[1] : null,
    stateUpper: mSLDU ? mSLDU[1] : null,
    stateCode: mState ? mState[1].toUpperCase() : null
  };
}

/**
 * Group representatives by type
 * @param {Array} items - Joined representatives data
 * @returns {Object} - Grouped representatives
 */
export function categorizeRepresentatives(items) {
  const result = {
    federal: {
      senators: [],
      representatives: []
    },
    state: {
      governor: null,
      statewide: []
    },
    local: []
  };

  for (const item of items) {
    // Get district information
    const district = extractDistrict(item.divisionId);
    const roleSet = new Set(item.roles || []);
    const levels = new Set(item.levels || []);
    
    // Categorize by role and level
    
    // Federal - Senators (upper body at country level)
    if (levels.has("country") && roleSet.has("legislatorUpperBody")) {
      result.federal.senators.push({
        ...item,
        position: "U.S. Senator",
        district: district?.stateCode
      });
    }
    // Federal - Representatives (lower body at country level)
    else if (levels.has("country") && roleSet.has("legislatorLowerBody")) {
      result.federal.representatives.push({
        ...item,
        position: "U.S. Representative",
        district: district?.usHouse ? `${district.stateCode}-${district.usHouse}` : district?.stateCode
      });
    }
    // State - Governor (headOfGovernment at administrativeArea1 level)
    else if (levels.has("administrativeArea1") && roleSet.has("headOfGovernment")) {
      result.state.governor = {
        ...item,
        position: "Governor",
        district: district?.stateCode
      };
    }
    // State - Other statewide officials
    else if (levels.has("administrativeArea1")) {
      // Add district information if available
      const statePosition = {
        ...item,
        position: item.officeName
      };
      
      if (roleSet.has("legislatorUpperBody")) {
        statePosition.position = "State Senator";
        statePosition.district = district?.stateUpper ? `${district.stateCode}-${district.stateUpper}` : district?.stateCode;
      } else if (roleSet.has("legislatorLowerBody")) {
        statePosition.position = "State Representative";
        statePosition.district = district?.stateLower ? `${district.stateCode}-${district.stateLower}` : district?.stateCode;
      }
      
      result.state.statewide.push(statePosition);
    }
    // Local officials (all other levels)
    else {
      result.local.push({
        ...item,
        role: item.officeName,
        position: item.officeName
      });
    }
  }
  
  return result;
}

/**
 * Cache representatives data by place_id
 * @param {string} placeId - Google Place ID
 * @param {Object} data - Representatives data
 */
export function cacheRepresentatives(placeId, data) {
  if (!placeId) return;
  
  try {
    const cache = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000) // 30 days
    };
    localStorage.setItem(`reps_cache_${placeId}`, JSON.stringify(cache));
  } catch (error) {
    console.error("Error caching representatives data:", error);
  }
}

/**
 * Get cached representatives data by place_id
 * @param {string} placeId - Google Place ID
 * @returns {Object|null} - Cached data or null if not found/expired
 */
export function getCachedRepresentatives(placeId) {
  if (!placeId) return null;
  
  try {
    const cacheJson = localStorage.getItem(`reps_cache_${placeId}`);
    if (!cacheJson) return null;
    
    const cache = JSON.parse(cacheJson);
    
    // Check if cache has expired
    if (cache.expiresAt && cache.expiresAt < Date.now()) {
      localStorage.removeItem(`reps_cache_${placeId}`);
      return null;
    }
    
    return cache.data;
  } catch (error) {
    console.error("Error reading cached representatives data:", error);
    return null;
  }
}

/**
 * Clear cached representatives data for a place_id
 * @param {string} placeId - Google Place ID
 */
export function clearCachedRepresentatives(placeId) {
  if (!placeId) return;
  
  try {
    localStorage.removeItem(`reps_cache_${placeId}`);
  } catch (error) {
    console.error("Error clearing cached representatives data:", error);
  }
}

/**
 * Format a date in a human-readable format
 * @param {Date|string|number} date - Date to format
 * @returns {string} - Formatted date
 */
export function formatDate(date) {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return '';
  }
}

/**
 * Mock representatives data for development/testing
 * @param {string} address - The address to generate mock data for
 * @returns {Object} - Mock civic API response
 */
export function generateMockCivicData(address) {
  // Extract state from address for basic customization
  const stateMatch = address.match(/([A-Z]{2})\s*\d{5}/) || address.match(/\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)\b/);
  const state = stateMatch ? stateMatch[1] : 'TX'; // Default to TX if no state found
  
  // City extraction (simple pattern)
  const cityMatch = address.match(/([A-Za-z\s]+),\s*[A-Z]{2}/);
  const city = cityMatch ? cityMatch[1].trim() : 'Houston';

  return {
    offices: [
      {
        name: "United States Senate",
        divisionId: `ocd-division/country:us/state:${state.toLowerCase()}`,
        levels: ["country"],
        roles: ["legislatorUpperBody"],
        officialIndices: [0, 1]
      },
      {
        name: "United States House of Representatives",
        divisionId: `ocd-division/country:us/state:${state.toLowerCase()}/cd:7`,
        levels: ["country"],
        roles: ["legislatorLowerBody"],
        officialIndices: [2]
      },
      {
        name: `${state} State Senate`,
        divisionId: `ocd-division/country:us/state:${state.toLowerCase()}/sldu:14`,
        levels: ["administrativeArea1"],
        roles: ["legislatorUpperBody"],
        officialIndices: [3]
      },
      {
        name: `${state} House of Representatives`,
        divisionId: `ocd-division/country:us/state:${state.toLowerCase()}/sldl:134`,
        levels: ["administrativeArea1"],
        roles: ["legislatorLowerBody"],
        officialIndices: [4]
      }
    ],
    officials: [
      {
        name: state === 'TX' ? 'Ted Cruz' : 'Senior Senator',
        party: 'Republican',
        phones: ['(202) 224-5922'],
        urls: ['https://cruz.senate.gov'],
        emails: ['contact@cruz.senate.gov'],
        photoUrl: 'https://www.cruz.senate.gov/wp-content/uploads/2022/08/TedCruz-Headshot-2022-scaled-e1660834963141.jpg'
      },
      {
        name: state === 'TX' ? 'John Cornyn' : 'Junior Senator',
        party: 'Republican',
        phones: ['(202) 224-2934'],
        urls: ['https://cornyn.senate.gov'],
        emails: ['contact@cornyn.senate.gov'],
        photoUrl: 'https://www.cornyn.senate.gov/wp-content/uploads/2022/08/Cornyn-Official-116th-Congress-Photo.jpg'
      },
      {
        name: state === 'TX' ? 'Lizzie Fletcher' : `Representative for ${city}`,
        party: 'Democrat',
        phones: ['(202) 225-2571'],
        urls: ['https://fletcher.house.gov'],
        emails: ['contact@fletcher.house.gov'],
        photoUrl: 'https://fletcher.house.gov/sites/evo-subsites/fletcher.house.gov/files/evo-media-image/Official%20Photo%20-%20117th%20Congress.jpg'
      },
      {
        name: state === 'TX' ? 'Paul Bettencourt' : `State Senator for ${city}`,
        party: 'Republican',
        phones: ['(512) 463-0107'],
        urls: [`https://senate.${state.toLowerCase()}.gov/member.php?d=7`],
        emails: [`paul.bettencourt@senate.${state.toLowerCase()}.gov`],
        photoUrl: 'https://senate.texas.gov/Images/Members/Headshots/Bettencourt_Paul_87.jpg'
      },
      {
        name: state === 'TX' ? 'Sarah Davis' : `State Representative for ${city}`,
        party: 'Republican',
        phones: ['(512) 463-0389'],
        urls: [`https://house.${state.toLowerCase()}.gov/members/member-page/?district=134`],
        emails: [`sarah.davis@house.${state.toLowerCase()}.gov`],
        photoUrl: 'https://tlc.texas.gov/Legislators/Members/MemberPictures/87/Davis_Sarah.jpg'
      }
    ],
    divisions: {
      [`ocd-division/country:us/state:${state.toLowerCase()}`]: {
        name: `${state} State`
      },
      [`ocd-division/country:us/state:${state.toLowerCase()}/cd:7`]: {
        name: `${state} 7th Congressional District`
      },
      [`ocd-division/country:us/state:${state.toLowerCase()}/sldu:14`]: {
        name: `${state} State Senate District 14`
      },
      [`ocd-division/country:us/state:${state.toLowerCase()}/sldl:134`]: {
        name: `${state} State House District 134`
      }
    }
  };
}

/**
 * Fallback function to get representatives with mock data when API is unavailable
 * @param {string} address - The address to get representatives for
 * @returns {Promise<Object>} - Mock representatives data
 */
export async function fetchRepresentativesWithFallback(address) {
  try {
    // Try to fetch from the real API first
    return await fetchCivicData(address);
  } catch (error) {
    console.warn("Civic API unavailable, using mock data:", error.message);
    
    // Return mock data as fallback
    return generateMockCivicData(address);
  }
}